import { useIntl } from '@umijs/max';
import 'pdfjs-dist/web/pdf_viewer.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyHighlightOverlays,
  applyHighlightsToPage,
  buildTextIndexWithOffsets,
  findChainedExactSpans,
  fuzzyFindInFullText,
  getMatchFragments,
  normaliseForMatch,
  normalizedTextSeparator,
  OffsetDivMapEntry,
  PageState,
  resolveSpanGeometry,
  TextMatchFragment,
  waitForContainerWidth,
} from './pdfHighlight.utils';

import { createPdfPageRenderer } from './pdfPageRenderer';

type ScrollMatchType = 'exact' | 'fuzzy' | 'none';

interface SearchPdfViewerWithHighlightProps {
  blobUrl: string;
  page: number;
  highlight?: string;
  height?: string;
  /** Paragraph text to scroll to (separate from highlight keyword) */
  scrollTarget?: string;
  /** Highlight all keyword matches instead of just the first (default: false) */
  highlightAll?: boolean;
  /** Called once the viewer determines whether the scrollTarget was an exact or fuzzy match */
  onScrollMatchType?: (type: ScrollMatchType) => void;
  /** Override overlay color decision from outside (salmon = exact, green = fuzzy) */
  scrollMatchType?: ScrollMatchType;
  /** Override scroll-target overlay color while retaining chained/fuzzy matching. */
  scrollTargetColor?: string;
  /** Zero-based keyword match to scroll to when every match is highlighted */
  highlightMatchIndex?: number;
  /** Limit scroll-target matching to the selected page and its continuation page. */
  scrollTargetPageOnly?: boolean;
}

const EXACT_COLOR = 'rgba(250, 163, 138, 0.55)';
const FUZZY_COLOR = 'rgba(134, 239, 172, 0.55)';

type PendingMatch = {
  placeholder: HTMLElement;
  fragments: TextMatchFragment[];
  pNum: number;
  vw: number;
};

function collectPageMatches(
  pNum: number,
  needle: string,
  pageStates: Map<number, PageState>,
  exactPending: PendingMatch[],
  fuzzyPending: PendingMatch[],
) {
  const state = pageStates.get(pNum);
  if (!state?.rendered || !state.textLayerDiv || !state.wrapper) return;
  const { fullText, divMap } = buildTextIndexWithOffsets(state.textLayerDiv);
  const vw =
    state.wrapper.offsetWidth ||
    parseFloat(state.wrapper.style.width) ||
    state.placeholder.offsetWidth ||
    600;

  const appendMatch = (matchIdx: number, matchEnd: number, target: PendingMatch[]) => {
    const fragments = getMatchFragments(divMap, matchIdx, matchEnd);
    if (fragments.length > 0) {
      target.push({ placeholder: state.placeholder, fragments, pNum, vw });
    }
  };

  let foundExact = false;
  let from = 0;
  while (from < fullText.length) {
    const idx = fullText.indexOf(needle, from);
    if (idx === -1) break;
    foundExact = true;
    appendMatch(idx, idx + needle.length, exactPending);
    from = idx + 1;
  }
  if (!foundExact) {
    const chained = findChainedExactSpans(fullText, needle);
    if (chained) {
      for (const span of chained.spans) appendMatch(span.start, span.end, exactPending);
    } else {
      const fuzzy = fuzzyFindInFullText(fullText, needle);
      if (fuzzy) appendMatch(fuzzy.matchIdx, fuzzy.matchEnd, fuzzyPending);
    }
  }
}

function collectCombinedPageMatches(
  pages: number[],
  needle: string,
  pageStates: Map<number, PageState>,
  exactPending: PendingMatch[],
  fuzzyPending: PendingMatch[],
) {
  let fullText = '';
  const entriesByPage = new Map<number, (OffsetDivMapEntry & { pNum: number })[]>();
  for (const pNum of pages) {
    const state = pageStates.get(pNum);
    if (!state?.rendered || !state.textLayerDiv) continue;
    const pageIndex = buildTextIndexWithOffsets(state.textLayerDiv);
    const separator = normalizedTextSeparator(fullText, pageIndex.fullText);
    const pageOffset = fullText.length + separator.length;
    entriesByPage.set(
      pNum,
      pageIndex.divMap.map((entry) => ({
        ...entry,
        pNum,
        start: entry.start + pageOffset,
        end: entry.end + pageOffset,
      })),
    );
    fullText += separator + pageIndex.fullText;
  }

  const appendMatches = (matchIdx: number, matchEnd: number, target: PendingMatch[]) => {
    for (const pNum of pages) {
      const pageEntries = entriesByPage.get(pNum) ?? [];
      const fragments = getMatchFragments(pageEntries, matchIdx, matchEnd);
      if (fragments.length === 0) continue;
      const state = pageStates.get(pNum);
      if (!state) continue;
      const wrapperWidth = state.wrapper?.offsetWidth ?? 0;
      const parsedWidth = parseFloat(state.wrapper?.style.width ?? '');
      const placeholderWidth = state.placeholder.offsetWidth;
      const vw = wrapperWidth || parsedWidth || placeholderWidth || 600;
      target.push({ placeholder: state.placeholder, fragments, pNum, vw });
    }
  };

  let foundExact = false;
  let from = 0;
  while (from < fullText.length) {
    const idx = fullText.indexOf(needle, from);
    if (idx === -1) break;
    foundExact = true;
    appendMatches(idx, idx + needle.length, exactPending);
    from = idx + 1;
  }
  if (!foundExact) {
    const chained = findChainedExactSpans(fullText, needle);
    if (chained) {
      for (const span of chained.spans) appendMatches(span.start, span.end, exactPending);
    } else {
      const fuzzy = fuzzyFindInFullText(fullText, needle);
      if (fuzzy) appendMatches(fuzzy.matchIdx, fuzzy.matchEnd, fuzzyPending);
    }
  }
}

function spanFromRange(
  idx: number,
  end: number,
  pageBounds: { pageNum: number; start: number; end: number }[],
): number[] {
  const startB = pageBounds.find((b) => b.end > idx);
  const endB = pageBounds.find((b) => b.end >= end);
  const startPage = startB?.pageNum ?? pageBounds[0]?.pageNum ?? 1;
  const endPage = endB?.pageNum ?? pageBounds[pageBounds.length - 1]?.pageNum ?? startPage;
  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
}

function findBoundedChainedSpan(
  fullText: string,
  needle: string,
  pageBounds: { pageNum: number; start: number; end: number }[],
  windowPages = 4,
): number[] | null {
  let best: { coverage: number; pages: number[] } | null = null;
  for (let start = 0; start < pageBounds.length; start++) {
    const window = pageBounds.slice(start, start + windowPages);
    const firstBound = window[0];
    const lastBound = window[window.length - 1];
    if (!firstBound || !lastBound) continue;
    const chained = findChainedExactSpans(fullText.slice(firstBound.start, lastBound.end), needle);
    if (!chained) continue;
    const first = chained.spans[0];
    const last = chained.spans[chained.spans.length - 1];
    const pages = spanFromRange(
      firstBound.start + first.start,
      firstBound.start + last.end,
      pageBounds,
    );
    if (
      !best ||
      chained.coverage > best.coverage ||
      (chained.coverage === best.coverage && pages.length < best.pages.length)
    ) {
      best = { coverage: chained.coverage, pages };
    }
  }
  return best?.pages ?? null;
}

async function findExactMatchSpan(
  needle: string,
  numPages: number,
  cancelled: () => boolean,
  readPageText: (pageNum: number) => Promise<string>,
): Promise<number[] | null> {
  const window: { pageNum: number; text: string }[] = [];
  let best: { coverage: number; pages: number[] } | null = null;
  for (let pageNum = 1; pageNum <= numPages; pageNum++) {
    if (cancelled()) return null;
    window.push({ pageNum, text: await readPageText(pageNum) });
    if (cancelled()) return null;
    // Retain enough text for a cross-page exact needle, and at least four
    // pages for the existing bounded chained rescue. Never collect the PDF.
    while (
      window.length > 4 &&
      window.slice(1).reduce((n, p) => n + p.text.length, 0) >= needle.length
    ) {
      window.shift();
    }
    let col = '';
    const bounds: { pageNum: number; start: number; end: number }[] = [];
    for (const entry of window) {
      const start = col.length + normalizedTextSeparator(col, entry.text).length;
      col += normalizedTextSeparator(col, entry.text) + entry.text;
      bounds.push({ pageNum: entry.pageNum, start, end: col.length });
    }
    const idx = col.indexOf(needle);
    if (idx !== -1) return spanFromRange(idx, idx + needle.length, bounds);
    const pages = findBoundedChainedSpan(col, needle, bounds);
    if (pages) {
      const first = bounds.find((b) => b.pageNum === pages[0])!;
      const last = bounds.find((b) => b.pageNum === pages[pages.length - 1])!;
      const match = findChainedExactSpans(col.slice(first.start, last.end), needle);
      if (
        match &&
        (!best ||
          match.coverage > best.coverage ||
          (match.coverage === best.coverage && pages.length < best.pages.length))
      ) {
        best = { coverage: match.coverage, pages };
      }
    }
    // Yield periodically so closing/changing the document cancels a scan.
    if (pageNum % 20 === 0)
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 0);
      });
  }
  return best?.pages ?? null;
}

/**
 * Last-resort fallback: scans a window of pages around the target (via pdf.js
 * getTextContent, no canvas render) for a FUZZY match, used only when neither
 * the initial page-window pass nor an exact scan found anything. The target
 * chunk's text may genuinely be present on/near the target page but diverge
 * enough from pdf.js's own extraction (OCR/extraction differences, legal
 * citations, subsection markers, etc.) that only a fuzzy match will succeed.
 * Bounded to a small window (not the whole document) to keep this cheap.
 */
async function findFuzzyMatchSpan(
  needle: string,
  numPages: number,
  targetPage: number,
  cancelled: () => boolean,
  readPageText: (pageNum: number) => Promise<string>,
  windowPages = 2,
): Promise<number[] | null> {
  const firstPage = Math.max(1, targetPage - windowPages);
  const lastPage = Math.min(numPages, targetPage + windowPages);

  let col = '';
  const pageBounds: { pageNum: number; start: number; end: number }[] = [];
  for (let pageNum = firstPage; pageNum <= lastPage; pageNum++) {
    if (cancelled()) return null;
    const pageText = await readPageText(pageNum);
    if (cancelled()) return null;
    const separator = normalizedTextSeparator(col, pageText);
    const pageStart = col.length + separator.length;
    pageBounds.push({ pageNum, start: pageStart, end: pageStart + pageText.length });
    col += separator + pageText;
  }
  const fuzzy = fuzzyFindInFullText(col, needle);
  if (!fuzzy) return null;
  return spanFromRange(fuzzy.matchIdx, fuzzy.matchEnd, pageBounds);
}

type FragmentRect = { top: number; left: number; right: number; height: number };

function getFragmentRects(fragment: TextMatchFragment, wrapper: HTMLElement): FragmentRect[] {
  const textNode = Array.from(fragment.div.childNodes).find(
    (node) => node.nodeType === Node.TEXT_NODE,
  );
  if (textNode?.textContent) {
    const range = document.createRange();
    if (
      typeof range.setStart === 'function' &&
      typeof range.setEnd === 'function' &&
      typeof range.getClientRects === 'function'
    ) {
      range.setStart(textNode, Math.min(fragment.startOffset, textNode.textContent.length));
      range.setEnd(textNode, Math.min(fragment.endOffset, textNode.textContent.length));
      const wrapperRect = wrapper.getBoundingClientRect();
      const clientRects = Array.from(range.getClientRects());
      if (clientRects.length > 0) {
        return clientRects.map((rect) => ({
          top: rect.top - (wrapperRect.top ?? 0),
          left: rect.left - (wrapperRect.left ?? 0),
          right: rect.right - (wrapperRect.left ?? 0),
          height: rect.height,
        }));
      }
    }
  }

  const { top, left, fontSize } = resolveSpanGeometry(fragment.div);
  const divRect = fragment.div.getBoundingClientRect();
  return [{ top, left, right: left + (divRect.width || fontSize), height: fontSize * 1.2 }];
}

function placeScrollOverlays(
  toShow: PendingMatch[],
  pageStates: Map<number, PageState>,
  overlayColor: string,
): { placeholder: HTMLElement; top: number }[] {
  const matches: { placeholder: HTMLElement; top: number }[] = [];
  for (const { placeholder, fragments, pNum, vw } of toShow) {
    const state = pageStates.get(pNum);
    if (!state?.wrapper) continue;
    const lineMap = new Map<number, FragmentRect>();
    for (const fragment of fragments) {
      for (const rect of getFragmentRects(fragment, state.wrapper)) {
        const key = Math.round(rect.top);
        const existing = lineMap.get(key);
        if (existing) {
          existing.left = Math.min(existing.left, rect.left);
          existing.right = Math.max(existing.right, rect.right);
          existing.height = Math.max(existing.height, rect.height);
        } else {
          lineMap.set(key, { ...rect });
        }
      }
    }

    let firstTop: number | null = null;
    for (const { top, height, left, right } of lineMap.values()) {
      const width = Math.min(Math.max(right - left, 1), Math.max(vw - left, 1));
      const el = document.createElement('div');
      el.className = 'pdf-scroll-highlight';
      el.style.cssText = `position:absolute;background:${overlayColor};pointer-events:none;left:${left}px;top:${top}px;width:${width}px;height:${height}px;`;
      state.wrapper.appendChild(el);
      if (firstTop === null) firstTop = top;
    }
    if (firstTop !== null) matches.push({ placeholder, top: firstTop });
  }
  return matches;
}

type ScrollLocation = { placeholder: HTMLElement; top: number };

function removeOverlays(wrapper: HTMLElement | undefined, selector: string) {
  wrapper?.querySelectorAll(selector).forEach((element) => element.remove());
}

function normalisePdfText(items: { str: string }[]) {
  return normaliseForMatch(items.map((item) => item.str).join(' '));
}

function orderedLocations(locations: Map<number, ScrollLocation[]>) {
  return Array.from(locations.entries())
    .sort(([a], [b]) => a - b)
    .flatMap(([, matches]) => matches);
}

function restoreScrollPage({
  pageNum,
  groups,
  states,
  needle,
  matchType,
  overlayColor,
  locations,
  overlayMarkup,
}: {
  pageNum: number;
  groups: number[][];
  states: Map<number, PageState>;
  needle: string;
  matchType: ScrollMatchType;
  overlayColor: string;
  locations: Map<number, ScrollLocation[]>;
  overlayMarkup: Map<number, string>;
}): boolean {
  const group = groups.find((pages) => pages.includes(pageNum));
  if (!group) return false;
  if (!group.every((page) => states.get(page)?.rendered)) {
    const wrapper = states.get(pageNum)?.wrapper;
    const markup = overlayMarkup.get(pageNum);
    if (wrapper && markup) {
      removeOverlays(wrapper, '.pdf-scroll-highlight');
      wrapper.insertAdjacentHTML('beforeend', markup);
    }
    return false;
  }
  const exact: PendingMatch[] = [];
  const fuzzy: PendingMatch[] = [];
  if (group.length === 1) collectPageMatches(pageNum, needle, states, exact, fuzzy);
  else collectCombinedPageMatches(group, needle, states, exact, fuzzy);
  const matches = matchType === 'exact' ? exact : fuzzy;
  for (const page of group) removeOverlays(states.get(page)?.wrapper, '.pdf-scroll-highlight');
  const placed = placeScrollOverlays(matches, states, overlayColor);
  for (const page of group) {
    const pageMatches = placed.filter(
      (match) => match.placeholder === states.get(page)?.placeholder,
    );
    if (pageMatches.length) locations.set(page, pageMatches);
    else locations.delete(page);
    const wrapper = states.get(page)?.wrapper;
    if (wrapper)
      overlayMarkup.set(
        page,
        Array.from(wrapper.querySelectorAll('.pdf-scroll-highlight'))
          .map((element) => element.outerHTML)
          .join(''),
      );
  }
  return true;
}

const waitForFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

const SearchPdfViewerWithHighlight: React.FC<SearchPdfViewerWithHighlightProps> = ({
  blobUrl,
  page,
  highlight,
  height = '400px',
  scrollTarget,
  highlightAll = false,
  onScrollMatchType,
  scrollMatchType: externalScrollMatchType,
  scrollTargetColor,
  highlightMatchIndex = 0,
  scrollTargetPageOnly = false,
}) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [placeholdersReady, setPlaceholdersReady] = useState(false);
  const [scrollMatchCount, setScrollMatchCount] = useState(0);
  const [scrollMatchIndex, setScrollMatchIndex] = useState(0);
  const scrollMatchesRef = useRef<{ placeholder: HTMLElement; top: number }[]>([]);

  const pdfDocRef = useRef<any>(null);
  const pageStatesRef = useRef<Map<number, PageState>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const rendererRef = useRef<ReturnType<typeof createPdfPageRenderer> | null>(null);
  const restoreOverlaysRef = useRef<(pageNum: number) => void>(() => {});
  const navigationRequestRef = useRef(0);

  const goToScrollMatch = async (newIndex: number) => {
    const container = containerRef.current;
    if (!container) return;
    const request = ++navigationRequestRef.current;
    const clamped = Math.max(0, Math.min(newIndex, scrollMatchesRef.current.length - 1));
    setScrollMatchIndex(clamped);
    const match = scrollMatchesRef.current[clamped];
    if (match) {
      const pageNum = Number(match.placeholder.getAttribute('data-page'));
      const renderer = rendererRef.current;
      await renderer?.render(pageNum);
      if (rendererRef.current !== renderer || navigationRequestRef.current !== request) return;
      const resolved = scrollMatchesRef.current[clamped] ?? match;
      const scrollTo = resolved.placeholder.offsetTop + resolved.top - container.clientHeight / 2;
      container.scrollTo({ top: Math.max(0, scrollTo), behavior: 'smooth' });
    }
  };

  const renderPage = useCallback(async (pageNum: number) => {
    await rendererRef.current?.render(pageNum);
  }, []);

  const unloadPage = useCallback((pageNum: number) => {
    rendererRef.current?.unload(pageNum);
  }, []);

  // Effect 1: Load PDF & create placeholders
  useEffect(() => {
    if (!blobUrl) return;

    let cancelled = false;
    let pdf: any = null;
    let pdfDestroyed = false;
    let loadingTask: any = null;
    setError(null);
    setScrollMatchCount(0);
    scrollMatchesRef.current = [];

    const destroyPdf = async () => {
      if (!pdfDestroyed && pdf && typeof pdf.destroy === 'function') {
        pdfDestroyed = true;
        await pdf.destroy();
      }
    };

    const setup = async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const { TextLayerBuilder } = await import('pdfjs-dist/web/pdf_viewer.mjs');
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url,
        ).toString();
        if (cancelled) return;

        const documentOptions = {
          url: blobUrl,
          isEvalSupported: false,
          enableScripting: false,
        } as Parameters<typeof pdfjs.getDocument>[0] & { enableScripting: boolean };
        loadingTask = pdfjs.getDocument(documentOptions);
        pdf = await loadingTask.promise;
        if (cancelled) {
          await destroyPdf();
          return;
        }
        pdfDocRef.current = pdf;

        const container = containerRef.current;
        if (!container) return;

        container.innerHTML = '';

        // Wait for container width
        const containerWidth = await waitForContainerWidth(containerRef);
        if (cancelled) return;

        // Compute scale from page 1
        const firstPage = await pdf.getPage(1);
        if (cancelled) return;
        const defaultViewport = firstPage.getViewport({ scale: 1 });
        const scale = containerWidth / defaultViewport.width;

        // Estimate dimensions from page 1; resolve each page only when needed.
        const viewport = firstPage.getViewport({ scale });
        const newPageStates = new Map<number, PageState>();
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const placeholder = document.createElement('div');
          placeholder.setAttribute('data-page', String(pageNum));
          placeholder.style.cssText = `
            width: ${viewport.width}px;
            height: ${viewport.height}px;
            margin-bottom: 4px;
            background: #fff;
          `;
          container.appendChild(placeholder);
          newPageStates.set(pageNum, { placeholder, rendered: false });
        }
        pageStatesRef.current = newPageStates;
        rendererRef.current = createPdfPageRenderer(
          pdf,
          TextLayerBuilder,
          newPageStates,
          container,
          scale,
          (pageNum) => restoreOverlaysRef.current(pageNum),
          (err) => {
            if (!cancelled) console.error('PDF page render error:', err);
          },
        );

        if (cancelled) return;

        // Set up IntersectionObserver
        const observer = new IntersectionObserver(
          (entries) => {
            if (cancelled) return;
            for (const entry of entries) {
              const num = parseInt(entry.target.getAttribute('data-page') || '0', 10);
              if (!num) continue;
              if (entry.isIntersecting) {
                void renderPage(num);
              } else {
                unloadPage(num);
              }
            }
          },
          {
            root: container,
            rootMargin: '100% 0px',
            threshold: 0,
          },
        );
        observerRef.current = observer;

        newPageStates.forEach((state) => {
          observer.observe(state.placeholder);
        });

        setPlaceholdersReady(true);
      } catch (err) {
        await destroyPdf();
        if (!cancelled) {
          console.error('PDF setup error:', err);
          setError(
            intl.formatMessage({
              id: 'components.searchPdfViewerWithHighlight.renderError',
              defaultMessage: 'Failed to render PDF',
            }),
          );
        }
      }
    };

    setup();

    return () => {
      cancelled = true;
      observerRef.current?.disconnect();
      observerRef.current = null;
      setPlaceholdersReady(false);
      pageStatesRef.current = new Map();
      rendererRef.current?.dispose();
      rendererRef.current = null;
      restoreOverlaysRef.current = () => {};
      pdfDocRef.current = null;
      if (pdf) void destroyPdf();
      else loadingTask?.destroy?.();
      const container = containerRef.current;
      if (container) container.innerHTML = '';
    };
  }, [blobUrl, renderPage, unloadPage]);

  // Effect 2: Scroll to page
  useEffect(() => {
    if (!placeholdersReady) return;

    const container = containerRef.current;
    if (!container) return;

    const target = container.querySelector(`[data-page="${page}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
      void renderPage(page);
    }
  }, [page, placeholdersReady]);

  // Effect 3: Apply highlights
  useEffect(() => {
    if (!placeholdersReady) return;
    setScrollMatchCount(0);
    scrollMatchesRef.current = [];
    navigationRequestRef.current++;
    if (!highlight && !scrollTarget) {
      containerRef.current
        ?.querySelectorAll('.pdf-highlight-overlay, .pdf-scroll-highlight')
        .forEach((el) => el.remove());
      return;
    }

    const cancelledRef = { current: false };

    const applyHighlights = async () => {
      // NOSONAR
      const container = containerRef.current;
      if (!container) return;

      const targetPage = Math.min(Math.max(1, page), pdfDocRef.current?.numPages ?? 1);
      await renderPage(targetPage);
      if (cancelledRef.current) return;

      await waitForFrame();
      if (cancelledRef.current) return;

      container
        .querySelectorAll('.pdf-highlight-overlay, .pdf-scroll-highlight')
        .forEach((el) => el.remove());

      const { firstHighlightTop, firstHighlightPage } = applyHighlightOverlays(
        highlightAll,
        targetPage,
        pageStatesRef.current,
        highlight,
      );

      const restoreKeyword = (n: number) => {
        if (cancelledRef.current) return;
        removeOverlays(pageStatesRef.current.get(n)?.wrapper, '.pdf-highlight-overlay');
        if (highlightAll || n === targetPage)
          applyHighlightsToPage(n, pageStatesRef.current, highlight, highlightAll);
      };

      if (scrollTarget) {
        const pdf = pdfDocRef.current;
        if (!pdf) return;
        const numPages = pdf.numPages;
        const needle = normaliseForMatch(scrollTarget);
        if (!needle) return;
        const cancelled = () => cancelledRef.current || pdfDocRef.current !== pdf;
        const readPageText = async (pageNum: number) => {
          if (cancelled()) return '';
          const pdfPage = await pdf.getPage(pageNum);
          if (cancelled()) return '';
          try {
            const text = await pdfPage.getTextContent();
            return normalisePdfText(text.items as { str: string }[]);
          } finally {
            // cleanup() defers if pdf.js is still rendering this page.
            if (!pageStatesRef.current.get(pageNum)?.rendered) pdfPage.cleanup?.();
          }
        };
        let groups: number[][] = [];
        const discoveredCounts = new Map<number, number>();
        let internalMatchType: ScrollMatchType = 'none';
        if (scrollTargetPageOnly) {
          const initial = [targetPage, targetPage + 1].filter((n) => n <= numPages);
          await Promise.all(initial.map((n) => renderPage(n)));
          if (cancelled()) return;
          await waitForFrame();
          if (cancelled()) return;
          const exact: PendingMatch[] = [];
          const fuzzy: PendingMatch[] = [];
          collectCombinedPageMatches(initial, needle, pageStatesRef.current, exact, fuzzy);
          if (exact.length) {
            groups = [initial];
            internalMatchType = 'exact';
          } else {
            const span = await findExactMatchSpan(needle, numPages, cancelled, readPageText);
            if (cancelled()) return;
            if (span) {
              groups = [span];
              internalMatchType = 'exact';
            } else if (fuzzy.length) {
              groups = [initial];
              internalMatchType = 'fuzzy';
            }
          }
        } else {
          // Discover match pages from text, never render a document just to search it.
          const exactGroups: number[][] = [];
          const fuzzyGroups: number[][] = [];
          for (let n = 1; n <= numPages; n++) {
            const text = await readPageText(n);
            if (cancelled()) return;
            let count = 0;
            for (let from = 0; from < text.length; ) {
              const idx = text.indexOf(needle, from);
              if (idx === -1) break;
              count++;
              from = idx + 1;
            }
            if (count || findChainedExactSpans(text, needle)) {
              exactGroups.push([n]);
              discoveredCounts.set(n, count || 1);
            } else if (fuzzyFindInFullText(text, needle)) fuzzyGroups.push([n]);
            if (n % 20 === 0)
              await new Promise<void>((resolve) => {
                setTimeout(resolve, 0);
              });
          }
          if (cancelled()) return;
          if (exactGroups.length) {
            groups = exactGroups;
            internalMatchType = 'exact';
          } else {
            const span = await findExactMatchSpan(needle, numPages, cancelled, readPageText);
            if (cancelled()) return;
            if (span) {
              groups = [span];
              internalMatchType = 'exact';
            } else if (fuzzyGroups.length) {
              groups = fuzzyGroups;
              internalMatchType = 'fuzzy';
            }
          }
        }
        if (!groups.length) {
          const span = await findFuzzyMatchSpan(
            needle,
            numPages,
            targetPage,
            cancelled,
            readPageText,
          );
          if (cancelled()) return;
          if (span) {
            groups = [span];
            internalMatchType = 'fuzzy';
          }
        }
        if (cancelled()) return;
        const effectiveMatchType = externalScrollMatchType ?? internalMatchType;
        const overlayColor =
          scrollTargetColor ?? (effectiveMatchType === 'fuzzy' ? FUZZY_COLOR : EXACT_COLOR);
        onScrollMatchType?.(internalMatchType);

        // Keep match locations as numbers/placeholder references, not detached text layers.
        const locations = new Map<number, { placeholder: HTMLElement; top: number }[]>();
        for (const group of groups) {
          for (const n of group) {
            const state = pageStatesRef.current.get(n);
            if (state) {
              locations.set(
                n,
                Array.from({ length: discoveredCounts.get(n) ?? 1 }, () => ({
                  placeholder: state.placeholder,
                  top: 0,
                })),
              );
            }
          }
        }
        let navigationReady = false;
        const updateLocations = () => {
          scrollMatchesRef.current = orderedLocations(locations);
          if (navigationReady) setScrollMatchCount(scrollMatchesRef.current.length);
        };
        const overlayMarkup = new Map<number, string>();
        const restore = (n: number) => {
          if (cancelled()) return;
          if (
            !restoreScrollPage({
              pageNum: n,
              groups,
              states: pageStatesRef.current,
              needle,
              matchType: internalMatchType,
              overlayColor,
              locations,
              overlayMarkup,
            })
          )
            return;
          updateLocations();
        };
        restoreOverlaysRef.current = (n) => {
          restoreKeyword(n);
          restore(n);
        };
        // Resolve only the first few matches now. Further matches render on scroll/navigation.
        const initialMatches = groups.flat().slice(0, 8);
        for (const n of initialMatches) {
          await renderPage(n);
          if (cancelled()) return;
        }
        await waitForFrame();
        if (cancelled()) return;
        groups.forEach((group) => restore(group[0]));
        navigationReady = true;
        updateLocations();
        setScrollMatchIndex(0);
        const first = scrollMatchesRef.current[0];
        if (first) {
          container.scrollTo({
            top: Math.max(0, first.placeholder.offsetTop + first.top - container.clientHeight / 2),
            behavior: 'auto',
          });
        } else {
          container
            .querySelector(`[data-page="${targetPage}"]`)
            ?.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
        return;
      }

      restoreOverlaysRef.current = restoreKeyword;
      if (firstHighlightTop !== null && firstHighlightPage !== null) {
        // No scrollTarget — scroll to first keyword highlight
        const state = pageStatesRef.current.get(firstHighlightPage);
        if (state) {
          const placeholderTop = state.placeholder.offsetTop;
          const scrollTo = placeholderTop + firstHighlightTop - container.clientHeight / 2;
          container.scrollTo({ top: Math.max(0, scrollTo), behavior: 'smooth' });
        }
      }
    };

    void applyHighlights().catch((err) => {
      if (!cancelledRef.current) console.error('PDF highlight error:', err);
    });
    return () => {
      cancelledRef.current = true;
      restoreOverlaysRef.current = () => {};
    };
  }, [
    page,
    highlight,
    scrollTarget,
    highlightAll,
    highlightMatchIndex,
    placeholdersReady,
    renderPage,
    scrollTargetColor,
    scrollTargetPageOnly,
  ]);

  if (error) {
    return <div style={{ padding: 16, color: 'red' }}>{error}</div>;
  }

  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      {scrollMatchCount > 1 && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            background: 'rgba(0,0,0,0.55)',
            color: '#fff',
            borderRadius: 6,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            userSelect: 'none',
          }}
        >
          <button
            type="button"
            style={{
              cursor: 'pointer',
              padding: '0 4px',
              background: 'none',
              border: 'none',
              fontSize: 'inherit',
            }}
            onClick={() => goToScrollMatch(scrollMatchIndex - 1)}
          >
            ▲
          </button>
          <span>
            {scrollMatchIndex + 1} / {scrollMatchCount}
          </span>
          <button
            type="button"
            style={{
              cursor: 'pointer',
              padding: '0 4px',
              background: 'none',
              border: 'none',
              fontSize: 'inherit',
            }}
            onClick={() => goToScrollMatch(scrollMatchIndex + 1)}
          >
            ▼
          </button>
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          overflowAnchor: 'none',
          background: '#f5f5f5',
        }}
      />
    </div>
  );
};

export default SearchPdfViewerWithHighlight;
